import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  Flex,
  Highlight,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";
import { useIntercom } from "react-use-intercom";
import {
  type AwardCriterionType,
  type LibraryElementContentDto,
  LibraryElementTypeDto,
  type LibraryElementDto,
  useListLibraryElementsQuery,
} from "../../autogen/bff-api";
import { useRequirementDescription } from "./describeRequirement";
import {
  AWARD_CRITERION_TYPE_OPTIONS,
  criterionTypeSelectValue,
  libraryHostCriterionType,
} from "../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { AwardCriterionFields } from "./fields/AwardCriterionFields";
import { AwardSubCriterionFields, SUB_CRITERION_HOST_TYPES } from "./fields/AwardSubCriterionFields";
import { MinimumRequirementFields } from "./fields/MinimumRequirementFields";
import { QualificationRequirementFields } from "./fields/QualificationRequirementFields";
import { RequirementDialogHeader } from "../components/RequirementDialogHeader";
import { RequirementTypeBadge } from "./RequirementTypeBadge";
import { SourceBadge } from "./SourceBadge";
import { useSaveToLibraryOption } from "./useSaveToLibraryOption";

// t('Untitled') t('Yes / No') t('Yes / No + comment')
// t('Requires documentation') t('weight {{n}} %') t('Search among {{count}} available')
// t('All') t('Own_plural') t('Standard') t('Source') t('Category')
// Own_plural, not Own: a row badge names one element ("Egen"), this names a group of them
// ("Egne"). Same convention as Active_plural.
// t('Recently added to the library') t('The rest of the library') t('Show {{count}} more')
// t('{{matched}} of {{total}} requirements match') t('Already in this section')
// t('{{count}} requirements are already in this section and are not shown here')
// t('Add manually') t('Add') t('Add {{count}}') t('No requirements match «{{query}}»')
// t('Try a shorter search, or create it now — it is saved to the library for later.')
// t('Create «{{query}}»') t('Clear the search and see all {{count}}')
// t('All {{count}} requirements in the library are already in this section')
// t('There are no more {{type}} to pick up. Write a new requirement to grow the library.')
// t('Write a new requirement') t('The library has no {{type}} yet')
// t('From the library ({{count}})') t('Write new')
// t('Add requirement') t('Cancel')

const DEFAULT_ROWS = 5;

type SourceFilter = "all" | "own" | "standard";

// The type row's "everything" value. Not an AwardCriterionType, and deliberately not the environment
// group's value either — those are the four families, this is the absence of a choice among them.
const ALL_TYPES = "ALL";

const FORM_ID = "add-requirement-panel-form";

// The category row carries a visible heading, so the group is named by it rather than repeating the
// word in an aria-label that could drift from what is on screen.
const CATEGORY_LABEL_ID = "add-requirement-panel-category";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: LibraryElementTypeDto;
  /** "Legg til minstekrav" — the section names itself, since only it knows the wording. */
  title: string;
  /** Explanatory prose under the title. */
  subtitle?: ReactNode;
  /** Plural, lowercase: "minstekrav". Used in the empty-state sentences. */
  typeNoun: string;
  /** Which tab opens — the section has one button per tab, so both are one click away either way. */
  initialTab: "library" | "new";
  /**
   * Hides the tab switcher and the shortcuts into the other tab, so the panel shows only what the
   * caller opened it for. Set by sections whose own buttons already made that choice; left off where a
   * single button opens the panel and the tabs are the only way to reach the other half.
   */
  lockedToInitialTab?: boolean;
  /** Requirements the section already holds. They are kept out of the list but shown, dimmed, in search. */
  isAlreadyInSection: (element: LibraryElementDto) => boolean;
  /** Both tabs land here: picked library elements and newly written ones arrive as the same content. */
  onAdd: (contents: LibraryElementContentDto[]) => void;
  /** Award criteria only: the types this section allows, since a template permits more than the library. */
  awardTypes?: AwardCriterionType[];
  /**
   * Restricts the list to one criterion family, collapsed the way `criterionTypeSelectValue` collapses
   * it. On a requirements panel it is the criterion the panel was opened from — the library then lists
   * the requirements that belong under that kind of criterion and stamps a newly written one with it,
   * so what is on offer depends on where you came from. On a criteria panel it is the family the caller
   * is looking for, which is how the § 5b block offers environment criteria and nothing else.
   */
  criterionType?: AwardCriterionType;
}

// Every library element is titled — the forms require it, because the title is what search matches on.
const labelOf = (e: LibraryElementDto): string =>
  e.content.minimum?.title ??
  e.content.selection?.name ??
  e.content.award?.name ??
  e.content.awardSubCriterion?.name ??
  "";

const GroupHeader = ({ children }: { children: React.ReactNode }) => (
  <Text
    px="4"
    py="2"
    bg="smBackgroundSecondary"
    borderBottomWidth="1px"
    borderColor="smBorder"
    fontSize="xs"
    fontWeight="semibold"
    letterSpacing="0.06em"
    textTransform="uppercase"
    color="smMuted"
  >
    {children}
  </Text>
);

const Row = ({
  element,
  detail,
  query,
  isSelected,
  isAlreadyAdded,
  onToggle,
}: {
  element: LibraryElementDto;
  detail: string;
  query: string;
  isSelected: boolean;
  isAlreadyAdded: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  const label = labelOf(element) || t("Untitled");
  const subtitle = isAlreadyAdded ? t("Already in this section") : detail;

  return (
    <Flex
      as={isAlreadyAdded ? "div" : "label"}
      data-testid="library-row"
      gap="3"
      px="4"
      py="3"
      borderBottomWidth="1px"
      borderColor="smBorder"
      bg={isSelected ? "smPrimarySofter" : undefined}
      opacity={isAlreadyAdded ? 0.6 : 1}
      cursor={isAlreadyAdded ? "default" : "pointer"}
      _hover={isAlreadyAdded ? undefined : { bg: isSelected ? "smPrimarySofter" : "smBackgroundSecondary" }}
    >
      {!isAlreadyAdded && (
        <Checkbox isChecked={isSelected} onChange={onToggle} colorScheme="teal" mt="0.5" flexShrink={0} />
      )}
      <Box minW="0" flex="1">
        <Flex align="center" gap="2" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="bold" color="smText">
            {query ? <Highlight query={query} styles={{ bg: "smWarningSoft" }}>{label}</Highlight> : label}
          </Text>
          {/* Before the source badge: which kind of requirement this is matters more to the choice than
              who owns it. Outside `subtitle`, so it survives "Already in this section". */}
          <RequirementTypeBadge content={element.content} />
          <SourceBadge global={element.owner.global} />
        </Flex>
        {subtitle && (
          <Text fontSize="xs" color="smMuted" mt="0.5" noOfLines={2}>
            {subtitle}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

/**
 * "Add from the library" as a browsable panel rather than a name lookup.
 *
 * The box it replaces only worked if you already knew what the requirement was called: an empty field
 * showed nothing, and a miss showed a bare "no matches". Here the list is the default — grouped, source
 * filtered, searchable — and both dead ends (no match, everything already used) lead somewhere.
 *
 * Selection is committed in one go, so several requirements can be picked in one visit.
 */
export const AddRequirementPanel = ({
  isOpen,
  onClose,
  type,
  title,
  subtitle,
  typeNoun,
  initialTab,
  lockedToInitialTab,
  isAlreadyInSection,
  onAdd,
  awardTypes,
  criterionType,
}: Props) => {
  const { t } = useTranslation();
  const describe = useRequirementDescription();
  const { update: updateIntercom } = useIntercom();
  const { data, isLoading } = useListLibraryElementsQuery({});
  const { option: saveToLibraryOption, reset: resetSaveToLibrary, saveIfChosen } = useSaveToLibraryOption(type);
  const [tabIndex, setTabIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  // The drawer keeps its content mounted when closed, so the write-new form has to be told to start
  // over — otherwise a half-typed requirement is still sitting there the next time it opens.
  const [formKey, setFormKey] = useState(0);

  // A fresh visit starts clean — a stale query or half-finished selection from last time is never what
  // the user meant to reopen.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSource("all");
      // Seeded from the family the panel is pinned to, rather than reset to "all": the list is already
      // narrowed to it, so leaving the chip on "Alle" made the panel describe itself wrongly.
      //
      // Only where the chips exist. A requirements panel is pinned too, but it has no chip row and its
      // elements carry no award content to match on — seeding there would filter every row away.
      setTypeFilter(
        criterionType && type === LibraryElementTypeDto.Award
          ? criterionTypeSelectValue(criterionType)
          : ALL_TYPES
      );
      setSelectedIds([]);
      setIsExpanded(false);
      resetSaveToLibrary();
      setTabIndex(initialTab === "new" ? 1 : 0);
      setFormKey((k) => k + 1);
    }
  }, [isOpen, initialTab, criterionType, type, resetSaveToLibrary]);

  // The Intercom launcher floats above everything, including this panel, where it lands right on top of
  // the "add" button. Same treatment as the wizard's own drawers: hide it while the panel is open.
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isOpen });
  }, [isOpen, updateIntercom]);

  const ofType = useMemo(() => {
    const all = (data?.elements ?? []).filter((e) => e.content.type === type);
    if (criterionType === undefined) return all;
    // The environment sub-types collapse to one family: a requirement filed under "Miljø" must show up
    // whichever of them the criterion currently carries (the BFF re-derives that from the weighting).
    const wanted = criterionTypeSelectValue(criterionType);
    return all.filter((e) => {
      // A whole criterion answers for its own type; a requirement answers for the criterion it is
      // filed under. Both collapse through the same family rule, so one filter covers the panel
      // opened from a criterion and the panel opened to find one.
      const awardType = e.content.award?.type;
      if (awardType != null) return criterionTypeSelectValue(awardType) === wanted;
      return (
        e.content.awardSubCriterion != null &&
        criterionTypeSelectValue(e.content.awardSubCriterion.criterionType) === wanted
      );
    });
  }, [data?.elements, type, criterionType]);
  const alreadyAdded = useMemo(() => ofType.filter(isAlreadyInSection), [ofType, isAlreadyInSection]);
  const available = useMemo(() => ofType.filter((e) => !isAlreadyInSection(e)), [ofType, isAlreadyInSection]);

  // Only award criteria carry a type worth filtering on. A sub-criterion panel is already pinned to one
  // criterion's family by `criterionType`, and the other requirement kinds have no such axis.
  const showTypeFilter = type === LibraryElementTypeDto.Award;

  // The one category on offer when the caller pinned the panel to a family, so the chip row says what
  // the list is actually showing rather than claiming "Alle".
  const pinnedTypeOption = useMemo(() => {
    if (criterionType === undefined || type !== LibraryElementTypeDto.Award) return undefined;
    const value = criterionTypeSelectValue(criterionType);
    return AWARD_CRITERION_TYPE_OPTIONS.find((option) => option.value === value);
  }, [criterionType, type]);

  const matchesSource = useCallback(
    (e: LibraryElementDto) => (source === "all" ? true : source === "own" ? !e.owner.global : e.owner.global),
    [source]
  );
  // Compared through criterionTypeSelectValue, so a criterion stored as an environment sub-type answers
  // to the one "Miljø" chip — its row badge may read "Miljøspesifikasjon", but filtering it away when
  // you pick Miljø would hide a row you can see.
  const matchesType = useCallback(
    (e: LibraryElementDto) =>
      typeFilter === ALL_TYPES ||
      (e.content.award != null && criterionTypeSelectValue(e.content.award.type) === typeFilter),
    [typeFilter]
  );

  const inType = useMemo(() => available.filter(matchesType), [available, matchesType]);
  const inSource = useMemo(() => inType.filter(matchesSource), [inType, matchesSource]);

  // Changing a filter drops whatever it hides from the selection. Ticking a row and then switching
  // filter used to leave it selected but off-screen: the footer went on counting it, there was no row
  // left to untick it with, and confirming added a requirement that was nowhere to be seen.
  //
  // The search box is deliberately not part of this. It narrows while you type on the way to a second
  // pick, so pruning on each keystroke would delete the first one — a filter is a deliberate change of
  // what you are looking at, a query is a way of getting to it.
  useEffect(() => {
    setSelectedIds((ids) => {
      const visible = new Set(inSource.map((e) => e.id));
      const kept = ids.filter((id) => visible.has(id));
      return kept.length === ids.length ? ids : kept;
    });
    // inSource is intentionally not a dependency: it changes identity on every library refetch, which
    // would prune a selection nobody touched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, typeFilter]);

  // Each row counts over the OTHER filter's current choice: "Miljø 3" while the source says "Egne" and
  // none of the three are own would be a number you cannot reach by clicking it.
  const counts = {
    all: inType.length,
    own: inType.filter((e) => !e.owner.global).length,
    standard: inType.filter((e) => e.owner.global).length,
  };
  const typeCounts = useMemo(() => {
    const bySource = available.filter(matchesSource);
    return {
      all: bySource.length,
      byValue: (value: string) =>
        bySource.filter(
          (e) => e.content.award != null && criterionTypeSelectValue(e.content.award.type) === value
        ).length,
    };
  }, [available, matchesSource]);

  const q = query.trim().toLowerCase();
  const matching = useCallback(
    (list: LibraryElementDto[]) =>
      q
        ? list.filter((e) => labelOf(e).toLowerCase().includes(q) || e.tags.some((tag) => tag.toLowerCase().includes(q)))
        : [],
    [q]
  );
  const found = useMemo(() => matching(inSource), [matching, inSource]);
  const foundAlreadyAdded = useMemo(() => matching(alreadyAdded), [matching, alreadyAdded]);

  // Newest first, so "recently added" is the default lens on a library nobody has memorised. Timestamps
  // are ISO, so comparing them as strings is chronological — and equal ones must compare equal, or the
  // sort has no defined answer for two requirements added in the same batch.
  const byNewest = useMemo(
    () => [...inSource].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [inSource]
  );
  const recent = byNewest.slice(0, DEFAULT_ROWS);
  const rest = byNewest.slice(DEFAULT_ROWS);

  const toggle = (id: string) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));

  // Closing comes first, both here and below: what was picked belongs to the section, and a row that
  // appears while the panel is still covering it reads as though the panel did not take the click.
  const commit = () => {
    const picked = available.filter((e) => selectedIds.includes(e.id));
    onClose();
    if (picked.length > 0) onAdd(picked.map((e) => e.content));
  };

  // A requirement written here always lands in the section; whether it also grows the library is the
  // admin's choice, and that write trails the panel rather than holding it open — see
  // `useSaveToLibraryOption`.
  const submitNew = async (content: LibraryElementContentDto) => {
    onClose();
    onAdd([content]);
    await saveIfChosen(content);
  };

  const everythingUsed = available.length === 0 && alreadyAdded.length > 0;
  const libraryEmpty = ofType.length === 0;
  const nothingFound = q.length > 0 && found.length === 0 && foundAlreadyAdded.length === 0;

  // The type is a badge on the row now, so the summary must not repeat it — it keeps the weight, the
  // documentation flag and the description, which is what the two clipped lines are worth spending on.
  const describeContent = (c: LibraryElementDto["content"]) => {
    if (c.minimum) return describe.minimum(c.minimum);
    if (c.selection) return describe.selection(c.selection, { titleShowsType: true });
    if (c.award) return describe.award(c.award, { titleShowsType: true });
    if (c.awardSubCriterion) return describe.awardSub(c.awardSubCriterion, { titleShowsType: true });
    return "";
  };

  const rowFor = (element: LibraryElementDto, opts: { isAlreadyAdded?: boolean; showAge?: boolean } = {}) => (
    <Row
      key={element.id}
      element={element}
      // Under "recently added" the age is the whole point of the grouping, so it joins the metadata.
      detail={[describeContent(element.content), opts.showAge ? moment(element.createdAt).fromNow() : ""]
        .filter(Boolean)
        .join(" · ")}
      query={q}
      isSelected={selectedIds.includes(element.id)}
      isAlreadyAdded={opts.isAlreadyAdded ?? false}
      onToggle={() => toggle(element.id)}
    />
  );

  const body = () => {
    if (isLoading) {
      return (
        <Flex justify="center" py="10">
          <Spinner color="smPrimary" />
        </Flex>
      );
    }

    if (libraryEmpty || everythingUsed) {
      return (
        <Box textAlign="center" px="6" py="10">
          <Text fontWeight="bold" mb="1">
            {libraryEmpty
              ? t("The library has no {{type}} yet", { type: typeNoun })
              : t("All {{count}} requirements in the library are already in this section", {
                  count: alreadyAdded.length,
                })}
          </Text>
          <Text fontSize="sm" color="smMuted" mb="5">
            {t("There are no more {{type}} to pick up. Write a new requirement to grow the library.", {
              type: typeNoun,
            })}
          </Text>
          {!lockedToInitialTab && (
            <Button colorScheme="teal" leftIcon={<Icon as={FiPlus} />} onClick={() => setTabIndex(1)}>
              {t("Write a new requirement")}
            </Button>
          )}
        </Box>
      );
    }

    if (nothingFound) {
      return (
        <Box textAlign="center" px="6" py="10">
          <Text fontWeight="bold" mb="1">
            {t("No requirements match «{{query}}»", { query: query.trim() })}
          </Text>
          <Text fontSize="sm" color="smMuted" mb="5">
            {t("Try a shorter search, or create it now — it is saved to the library for later.")}
          </Text>
          <Flex gap="2" justify="center" wrap="wrap">
            {!lockedToInitialTab && (
              <Button colorScheme="teal" leftIcon={<Icon as={FiPlus} />} onClick={() => setTabIndex(1)}>
                {t("Create «{{query}}»", { query: query.trim() })}
              </Button>
            )}
            {/* Clearing the search leaves the chips alone, so this promises what the filters still allow. */}
            <Button variant="outline" onClick={() => setQuery("")}>
              {t("Clear the search and see all {{count}}", { count: inSource.length })}
            </Button>
          </Flex>
        </Box>
      );
    }

    if (q) {
      return (
        <>
          <Text px="4" py="2" fontSize="xs" color="smMuted">
            {t("{{matched}} of {{total}} requirements match", { matched: found.length, total: inSource.length })}
          </Text>
          {found.map((e) => rowFor(e))}
          {foundAlreadyAdded.map((e) => rowFor(e, { isAlreadyAdded: true }))}
        </>
      );
    }

    return (
      <>
        <GroupHeader>{t("Recently added to the library")}</GroupHeader>
        {recent.map((e) => rowFor(e, { showAge: true }))}
        {rest.length > 0 &&
          (isExpanded ? (
            <>
              <GroupHeader>{t("The rest of the library")}</GroupHeader>
              {rest.map((e) => rowFor(e))}
            </>
          ) : (
            <Button variant="link" colorScheme="teal" size="sm" m="4" onClick={() => setIsExpanded(true)}>
              {t("Show {{count}} more", { count: rest.length })}
            </Button>
          ))}
        {alreadyAdded.length > 0 && (
          <Text px="4" py="3" fontSize="xs" color="smMuted">
            {t("{{count}} requirements are already in this section and are not shown here", {
              count: alreadyAdded.length,
            })}
          </Text>
        )}
      </>
    );
  };

  // The count rides along in the button's text, so its accessible name stays "Egne 4" — it is part of
  // the choice, not decoration. The space keeps the two apart when read out; the margin does it on
  // screen, because a Chakra button lays its children out as flex items and collapses the space away.
  const withCount = (label: string, n: number) => (
    <>
      {label}{" "}
      <Text as="span" ml="1" fontWeight="normal" opacity={0.65} fontSize="xs">
        {n}
      </Text>
    </>
  );

  // The two rows filter on different axes and must not read as one control: source is the lens on the
  // library, category narrows within it. Underlined text for the first, pills for the second — the shape
  // difference is what stops "Alle 5" above "Alle 5" from looking like one row wrapped in two.
  const sourceTab = (value: SourceFilter, label: string, n: number) => {
    const isActive = source === value;
    return (
      <Button
        key={value}
        variant="unstyled"
        height="auto"
        borderRadius="0"
        px="0"
        pb="1"
        fontSize="sm"
        fontWeight={isActive ? "bold" : "medium"}
        color={isActive ? "smText" : "smMuted"}
        borderBottomWidth="2px"
        borderBottomColor={isActive ? "smPrimary" : "transparent"}
        _hover={{ color: "smText" }}
        aria-pressed={isActive}
        onClick={() => setSource(value)}
      >
        {withCount(label, n)}
      </Button>
    );
  };

  const typeChip = (value: string, label: string, n: number) => {
    const isActive = typeFilter === value;
    return (
      <Button
        key={value}
        size="xs"
        borderRadius="full"
        variant={isActive ? "solid" : "outline"}
        colorScheme={isActive ? "teal" : "gray"}
        aria-pressed={isActive}
        onClick={() => setTypeFilter(value)}
      >
        {withCount(label, n)}
      </Button>
    );
  };

  // Nothing to search through means no search field — the state says everything, and an input that can
  // only ever return nothing is worse than none.
  const showSearch = !libraryEmpty && !everythingUsed;

  const newRequirementForm = () => {
    if (type === LibraryElementTypeDto.Minimum) {
      return (
        <MinimumRequirementFields
          key={formKey}
          formId={FORM_ID}
          onSubmit={(minimum) => submitNew({ type, minimum })}
        >
          {saveToLibraryOption}
        </MinimumRequirementFields>
      );
    }
    if (type === LibraryElementTypeDto.Selection) {
      return (
        <QualificationRequirementFields
          key={formKey}
          formId={FORM_ID}
          onSubmit={(selection) => submitNew({ type, selection })}
        >
          {saveToLibraryOption}
        </QualificationRequirementFields>
      );
    }
    if (type === LibraryElementTypeDto.AwardSubCriterion) {
      return (
        <AwardSubCriterionFields
          key={formKey}
          formId={FORM_ID}
          // Opened from a criterion, that criterion is the answer; opened from the library page, the
          // author has to say where the requirement belongs.
          criterionTypes={criterionType ? undefined : SUB_CRITERION_HOST_TYPES}
          // Written here, the requirement is kept on its own — and alone it has no siblings to be a
          // share of. A weight belongs to a requirement filed inside a criterion.
          weight="hidden"
          onSubmit={({ criterionType: chosen, ...sub }) =>
            submitNew({
              type,
              awardSubCriterion: {
                // The criterion a requirement was opened from may carry a sub-type the library will not
                // host — see `libraryHostCriterionType`.
                criterionType: libraryHostCriterionType((criterionType ?? chosen) as AwardCriterionType),
                name: sub.name ?? "",
                description: sub.description,
                answerFormat: sub.answerFormat,
                requiresDocumentation: sub.requiresDocumentation,
              },
            })
          }
        >
          {saveToLibraryOption}
        </AwardSubCriterionFields>
      );
    }
    return (
      <AwardCriterionFields
        key={formKey}
        formId={FORM_ID}
        allowedTypes={awardTypes}
        onSubmit={(award) => submitNew({ type, award })}
      >
        {saveToLibraryOption}
      </AwardCriterionFields>
    );
  };

  const isLibraryTab = tabIndex === 0;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <RequirementDialogHeader title={title} subtitle={subtitle}>
          {!lockedToInitialTab && (
            <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="teal">
              <TabList borderBottomWidth="0">
                {/* Same key as the empty state's button — one label for one destination. It counts the
                    whole library, not the slice the chips currently allow. */}
                <Tab fontSize="sm">
                  {t("From the library ({{count}})", { count: available.length })}
                </Tab>
                <Tab fontSize="sm">{t("Write new")}</Tab>
              </TabList>
            </Tabs>
          )}
        </RequirementDialogHeader>

        {isLibraryTab ? (
          <>
            {showSearch && (
              <Box px="6" pt="4" pb="3" borderBottomWidth="1px" borderColor="smBorder">
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="smMuted" />
                  </InputLeftElement>
                  <Input
                    autoFocus
                    autoComplete="off"
                    data-1p-ignore
                    borderRadius="md"
                    borderColor="smBorder"
                    _focusVisible={{ borderColor: "smPrimary", boxShadow: "none" }}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Search among {{count}} available", { count: inSource.length }) ?? ""}
                  />
                </InputGroup>
                {/* Both rows can start with "Alle N", so each names the axis it filters on for anyone
                    who cannot see that one is underlined text and the other a row of pills. */}
                <Flex
                  gap="5"
                  mt="3"
                  borderBottomWidth="1px"
                  borderColor="smBorder"
                  role="group"
                  aria-label={t("Source") ?? undefined}
                >
                  {sourceTab("all", t("All"), counts.all)}
                  {sourceTab("own", t("Own_plural"), counts.own)}
                  {sourceTab("standard", t("Standard"), counts.standard)}
                </Flex>
                {showTypeFilter && (
                  <Box mt="3">
                    <Text
                      id={CATEGORY_LABEL_ID}
                      fontSize="xs"
                      fontWeight="semibold"
                      letterSpacing="0.06em"
                      textTransform="uppercase"
                      color="smMuted"
                      mb="2"
                    >
                      {t("Category")}
                    </Text>
                    <Flex gap="2" wrap="wrap" role="group" aria-labelledby={CATEGORY_LABEL_ID}>
                      {pinnedTypeOption ? (
                        // Pinned: the caller opened this panel to find one family, and `ofType` has
                        // already narrowed to it. Offering the others would either lie about what the
                        // list can show or hand back a criterion the caller cannot use.
                        typeChip(
                          pinnedTypeOption.value,
                          t(pinnedTypeOption.labelKey),
                          typeCounts.byValue(pinnedTypeOption.value)
                        )
                      ) : (
                        <>
                          {typeChip(ALL_TYPES, t("All"), typeCounts.all)}
                          {AWARD_CRITERION_TYPE_OPTIONS.map((option) =>
                            typeChip(option.value, t(option.labelKey), typeCounts.byValue(option.value))
                          )}
                        </>
                      )}
                    </Flex>
                  </Box>
                )}
              </Box>
            )}

            <DrawerBody p="0">{body()}</DrawerBody>

            {/* The footer always stands, because Cancel is a way out and a way out is not conditional.
                Only the primary comes and goes: with an empty library, or everything already added,
                there is nothing to commit and the state's own action points at the other tab. */}
            <DrawerFooter borderTopWidth="1px" borderColor="smBorder" gap="3">
              <Button variant="outline" onClick={onClose}>
                {t("Cancel")}
              </Button>
              {showSearch && (
                <Button colorScheme="teal" onClick={commit} isDisabled={selectedIds.length === 0}>
                  {/* "Legg til 0" reads as an instruction to add nothing. With nothing picked the
                      button is disabled anyway, so it just names the action. */}
                  {selectedIds.length > 0 ? t("Add {{count}}", { count: selectedIds.length }) : t("Add")}
                </Button>
              )}
            </DrawerFooter>
          </>
        ) : (
          <>
            <DrawerBody pt="5">{newRequirementForm()}</DrawerBody>
            <DrawerFooter borderTopWidth="1px" borderColor="smBorder" gap="3">
              <Button variant="outline" onClick={onClose}>
                {t("Cancel")}
              </Button>
              <Button type="submit" form={FORM_ID} colorScheme="teal">
                {t("Add requirement")}
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};
